import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixAcademyCertificationsV1AccountCertification from './academy-certifications-v1-account-certification-account-certifications.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/**
 * An account certification represents a Wix Academy certification held by an account,
 * such as the Velo, Web Designer, or Wix Studio Developer certification.
 *
 * An account holds a certification when one or more of its team members hold the corresponding
 * user certification. Each account has at most one account certification per certification type,
 * aggregating all of the account's certified team members for that type.
 *
 * To read certifications at the individual user level, see the User Certifications API.
 */
export interface AccountCertification {
  /**
   * Account certification ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the account certification is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the account certification.
   *
   * Ignored when creating an account certification.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the account certification was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the account certification was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * IDs of the user certification records, held by the account's team members, that qualify the
   * account for its `certificationType`. Up to 100. The list contains no duplicates, and its order isn't guaranteed.
   * @format GUID
   * @maxSize 100
   */
  userCertificationIds?: string[];
  /**
   * Type of certification the account holds. Set when the certification is created and can't be changed afterward.
   * @immutable
   */
  certificationType?: CertificationTypeWithLiterals;
}

export enum CertificationType {
  /** Velo certification: proficiency in building with Velo, Wix's full-stack development platform. */
  VELO = 'VELO',
  /** Web Designer certification: proficiency in designing and building Wix websites. */
  WEB_DESIGNER = 'WEB_DESIGNER',
  /** Accessibility certification: proficiency in building accessible Wix websites. */
  ACCESSIBILITY = 'ACCESSIBILITY',
  /** Wix Studio Developer certification: proficiency in developing with Wix Studio. */
  STUDIO_DEVELOPER = 'STUDIO_DEVELOPER',
  /** Wix Studio Design League certification, awarded through the Wix Studio Design League program. */
  STUDIO_DESIGN_LEAGUE = 'STUDIO_DESIGN_LEAGUE',
  /** Wix Studio Developer Websites League certification, awarded through the Wix Studio Developer Websites League program. */
  STUDIO_DEV_WEBSITES_LEAGUE = 'STUDIO_DEV_WEBSITES_LEAGUE',
}

/** @enumType */
export type CertificationTypeWithLiterals =
  | CertificationType
  | 'VELO'
  | 'WEB_DESIGNER'
  | 'ACCESSIBILITY'
  | 'STUDIO_DEVELOPER'
  | 'STUDIO_DESIGN_LEAGUE'
  | 'STUDIO_DEV_WEBSITES_LEAGUE';

export interface CertifyAccountRequest {
  /**
   * ID of the user certification to add to the account.
   * @format GUID
   */
  userCertificationId?: string;
  /**
   * ID of the account to certify.
   * @format GUID
   */
  accountId?: string;
}

export interface CertifyAccountResponse {
  /** The created or updated account certification. */
  accountCertification?: AccountCertification;
}

export interface DecertifyAccountRequest {
  /**
   * ID of the user certification to remove from the account.
   * @format GUID
   */
  userCertificationId?: string;
  /**
   * ID of the account to decertify.
   * @format GUID
   */
  accountId?: string;
}

export interface DecertifyAccountResponse {
  /** Updated account certification. Empty when the last user certification of that type was removed and the account certification was deleted. */
  accountCertification?: AccountCertification;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface QueryAccountCertificationsRequest {
  /** Query options. See [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language) for more information. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 4
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 50
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Number of items to load.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * You can get the relevant cursor token
   * from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryAccountCertificationsResponse {
  /** List of account certifications. */
  accountCertifications?: AccountCertification[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  cursors?: Cursors;
  /**
   * Indicates if there are more results after the current page.
   * If `true`, another page of results can be retrieved.
   * If `false`, this is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor pointing to next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface ListAccountCertificationsRequest {
  /**
   * ID of the account to get certifications for.
   * @format GUID
   */
  accountId: string;
}

export interface ListAccountCertificationsResponse {
  /** The account's certifications, as a public, visitor-safe projection. */
  certifications?: Certification[];
}

/** A public, visitor-safe view of a certification held by an account. */
export interface Certification {
  /** Type of certification the account holds. */
  certificationType?: CertificationTypeWithLiterals;
  /**
   * Date and time the account certification was created.
   * @readonly
   */
  _createdDate?: Date | null;
}

export interface TeamMemberRemovedEvent {
  /**
   * ID of the account the user was removed from.
   * @format GUID
   */
  accountId?: string;
  /**
   * ID of the user (team member) that was removed from the account. Same identifier as `UserCertification.userId`.
   * @format GUID
   */
  teamMemberId?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/**
 * Retrieves a list of up to 1,000 account certifications, given the provided paging, filtering, and sorting.
 *
 * Query Account Certifications returns the full account certification records, including the underlying `userCertificationIds`, for the caller's own account only. To retrieve any account's certifications as a public, visitor-safe projection, call [List Account Certifications](https://dev.wix.com/docs/api-reference/account-level/studio-workspace/certifications/account-certification-v1/list-account-certifications) instead.
 *
 * Query Account Certifications runs with these defaults, which you can override:
 *
 * - `createdDate` is sorted in `DESC` order
 * - `cursorPaging.limit` is `50`
 *
 * You can filter and sort by `id`, `certificationType`, and `createdDate`.
 *
 * To learn about working with Query methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language), [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging), and [Field Projection](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-field-projection).
 * @public
 * @documentationMaturity preview
 * @permissionId ACADEMY.ACCOUNT_CERTIFICATION_READ
 * @applicableIdentity APP
 * @fqn wix.academy.certifications.account.v1.AccountCertifications.QueryAccountCertifications
 */
export function queryAccountCertifications(): AccountCertificationsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    AccountCertification,
    'CURSOR',
    QueryAccountCertificationsRequest,
    QueryAccountCertificationsResponse
  >({
    func: async (payload: QueryAccountCertificationsRequest) => {
      const reqOpts =
        ambassadorWixAcademyCertificationsV1AccountCertification.queryAccountCertifications(
          payload
        );

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryAccountCertificationsRequest['query']) => {
      const args = [query, {}] as [
        QueryAccountCertificationsRequest['query'],
        {}
      ];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryAccountCertificationsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.accountCertifications,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface AccountCertificationsQueryResult extends QueryCursorResult {
  items: AccountCertification[];
  query: AccountCertificationsQueryBuilder;
  next: () => Promise<AccountCertificationsQueryResult>;
  prev: () => Promise<AccountCertificationsQueryResult>;
}

export interface AccountCertificationsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  eq: (
    propertyName: '_id' | '_createdDate' | 'certificationType',
    value: any
  ) => AccountCertificationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ne: (
    propertyName: '_id' | '_createdDate' | 'certificationType',
    value: any
  ) => AccountCertificationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ge: (
    propertyName: '_id' | '_createdDate',
    value: any
  ) => AccountCertificationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  gt: (
    propertyName: '_id' | '_createdDate',
    value: any
  ) => AccountCertificationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  le: (
    propertyName: '_id' | '_createdDate',
    value: any
  ) => AccountCertificationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  lt: (
    propertyName: '_id' | '_createdDate',
    value: any
  ) => AccountCertificationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   * @documentationMaturity preview
   */
  startsWith: (
    propertyName: '_id',
    value: string
  ) => AccountCertificationsQueryBuilder;
  /** @documentationMaturity preview */
  in: (
    propertyName: '_id' | '_createdDate' | 'certificationType',
    value: any
  ) => AccountCertificationsQueryBuilder;
  /** @documentationMaturity preview */
  exists: (
    propertyName: '_id' | '_createdDate' | 'certificationType',
    value: boolean
  ) => AccountCertificationsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  ascending: (
    ...propertyNames: Array<'_id' | '_createdDate' | 'certificationType'>
  ) => AccountCertificationsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  descending: (
    ...propertyNames: Array<'_id' | '_createdDate' | 'certificationType'>
  ) => AccountCertificationsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object.
   * @documentationMaturity preview
   */
  limit: (limit: number) => AccountCertificationsQueryBuilder;
  /** @param cursor - A pointer to specific record
   * @documentationMaturity preview
   */
  skipTo: (cursor: string) => AccountCertificationsQueryBuilder;
  /** @documentationMaturity preview */
  find: () => Promise<AccountCertificationsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.academy.certifications.account.v1.AccountCertifications.QueryAccountCertifications
 * @requiredField query
 */
export async function typedQueryAccountCertifications(
  query: AccountCertificationQuery
): Promise<
  NonNullablePaths<
    QueryAccountCertificationsResponse,
    | `accountCertifications`
    | `accountCertifications.${number}.certificationType`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixAcademyCertificationsV1AccountCertification.queryAccountCertifications(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AccountCertificationQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: ['_createdDate', '_id', 'certificationType'];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  AccountCertification,
  AccountCertificationQuerySpec
>;
export type AccountCertificationQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Number of items to load. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  You can get the relevant cursor token
  from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object in the following format:
  `"filter" : {
  "fieldName1": "value1",
  "fieldName2":{"$operator":"value2"}
  }`
  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object in the following format:
  `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]` 
  @maxSize: 4 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 50 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: /*#__PURE__*/ createQueryUtils<
    AccountCertification,
    AccountCertificationQuerySpec,
    AccountCertificationQuery
  >(),
};

/**
 * Retrieves an account's certifications for public display, given the account ID.
 *
 * This is a public, visitor-safe projection that returns only each certification's type and
 * creation date. To retrieve the full account certification records, including the underlying
 * `userCertificationIds`, call [Query Account Certifications](https://dev.wix.com/docs/api-reference/account-level/studio-workspace/certifications/account-certification-v1/query-account-certifications) instead.
 * @param accountId - ID of the account to get certifications for.
 * @public
 * @documentationMaturity preview
 * @requiredField accountId
 * @fqn wix.academy.certifications.account.v1.AccountCertifications.ListAccountCertifications
 */
export async function listAccountCertifications(
  accountId: string
): Promise<
  NonNullablePaths<
    ListAccountCertificationsResponse,
    `certifications` | `certifications.${number}.certificationType`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    accountId: accountId,
  });

  const reqOpts =
    ambassadorWixAcademyCertificationsV1AccountCertification.listAccountCertifications(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { accountId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['accountId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
